import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Hammer, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../lib/auth';
import { extractError } from '../lib/api';

export default function OnboardingPage() {
  const { onboarding } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', password2: '' });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error('Passwörter stimmen nicht überein.'); return; }
    if (form.password.length < 10 || !/\d/.test(form.password) || !/[a-zA-Z]/.test(form.password)) {
      toast.error('Passwort muss mindestens 10 Zeichen, eine Zahl und einen Buchstaben enthalten.'); return;
    }
    setLoading(true);
    try {
      await onboarding({ first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password });
      toast.success('Administrator erfolgreich angelegt. Willkommen!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 w-14 h-14 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Hammer size={28} /></div>
          <CardTitle className="font-display">Willkommen</CardTitle>
          <CardDescription>Bitte richten Sie den ersten Administrator-Account ein.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Vorname</Label>
                <Input required value={form.first_name} onChange={e => update('first_name', e.target.value)} data-testid="onboarding-firstname-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Nachname</Label>
                <Input required value={form.last_name} onChange={e => update('last_name', e.target.value)} data-testid="onboarding-lastname-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>E-Mail-Adresse</Label>
              <Input type="email" required value={form.email} onChange={e => update('email', e.target.value)} data-testid="onboarding-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Passwort</Label>
                <Input type="password" required value={form.password} onChange={e => update('password', e.target.value)} data-testid="onboarding-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Passwort wiederholen</Label>
                <Input type="password" required value={form.password2} onChange={e => update('password2', e.target.value)} data-testid="onboarding-password2-input" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Mindestens 10 Zeichen, eine Zahl und ein Buchstabe.</p>
            <Button type="submit" className="w-full" disabled={loading} data-testid="onboarding-submit-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Administrator anlegen & App starten
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
